import type {
  ChangePrestationAccountData,
  CreatedPrestationParticipation,
  CreatePrestationData,
  CreatePrestationParticipationData,
  EcrituresRewrite,
  PartialPrestationData,
  PrestationParticipationWithRelations,
  PrestationPriceData,
  SendParticipantsEmailData,
  UpdateParticipantData,
  UpdatePrestationParticipationData,
  UpdatePrestationPriceData,
} from '@cpce/shared';
import { commiteeMutation, MutationMethod } from '../mutations.utils';
import { ECRITURES_REWRITE_INVALIDATIONS } from '../transactions/transactions.query-keys';
import { prestationsQueryKeys } from './prestations.query-keys';

export function useCreatePrestation() {
  return commiteeMutation<{ id: number }, CreatePrestationData, CreatePrestationData>({
    url: '/commitee/:commiteeId/prestations',
    method: MutationMethod.POST,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
  });
}

export function useUpdatePrestation(id: number) {
  return commiteeMutation<void, PartialPrestationData, PartialPrestationData>({
    url: `/commitee/:commiteeId/prestations/${id}`,
    method: MutationMethod.PUT,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
  });
}

export function useSetPrestationArchived(id: number) {
  return commiteeMutation<void, { archived: boolean }, boolean>({
    url: `/commitee/:commiteeId/prestations/${id}/archived`,
    method: MutationMethod.PATCH,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (archived) => ({ archived }),
    successMessage: (archived) => (archived ? 'Prestation archivée' : 'Prestation désarchivée'),
  });
}

export function useCreatePrestationPrice(prestationId: number) {
  return commiteeMutation<{ id: number }, PrestationPriceData, PrestationPriceData>({
    url: `/commitee/:commiteeId/prestations/${prestationId}/prices`,
    method: MutationMethod.POST,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
  });
}

export function useUpdatePrestationPrice(prestationId: number, priceId: number) {
  return commiteeMutation<{ id: number }, UpdatePrestationPriceData, UpdatePrestationPriceData>({
    url: `/commitee/:commiteeId/prestations/${prestationId}/prices/${priceId}`,
    method: MutationMethod.PUT,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
  });
}

export function useDeletePrestationPrice(prestationId: number, priceId: number) {
  return commiteeMutation<void>({
    url: `/commitee/:commiteeId/prestations/${prestationId}/prices/${priceId}`,
    method: MutationMethod.DELETE,
    invalidateQueries: [prestationsQueryKeys.all],
  });
}

export function useCreatePrestationParticipations() {
  return commiteeMutation<
    CreatedPrestationParticipation[],
    CreatePrestationParticipationData,
    CreatePrestationParticipationData
  >({
    url: '/commitee/:commiteeId/prestations/participations',
    method: MutationMethod.POST,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
    toastError: false,
  });
}

export function useUpdatePrestationParticipation(id: number) {
  return commiteeMutation<
    PrestationParticipationWithRelations,
    UpdatePrestationParticipationData,
    UpdatePrestationParticipationData
  >({
    url: `/commitee/:commiteeId/prestations/participations/${id}`,
    method: MutationMethod.PUT,
    invalidateQueries: [prestationsQueryKeys.all],
    body: (data) => data,
  });
}

export function useUpdateParticipant(participationId: number) {
  return commiteeMutation<
    void,
    UpdateParticipantData,
    { participantId: number } & UpdateParticipantData
  >({
    url: ({ participantId }) =>
      `/commitee/:commiteeId/prestations/participations/${participationId}/participants/${participantId}`,
    method: MutationMethod.PATCH,
    invalidateQueries: [prestationsQueryKeys.all],
    // Only the provided fields are sent; the server leaves the others untouched.
    body: ({ prestationPriceId, typedAmount, quantity }) => ({
      ...(prestationPriceId !== undefined && { prestationPriceId }),
      ...(typedAmount !== undefined && { typedAmount }),
      ...(quantity !== undefined && { quantity }),
    }),
    successMessage: 'Participant mis à jour',
  });
}

export function useCancelPrestationParticipation(id: number) {
  return commiteeMutation<void>({
    url: `/commitee/:commiteeId/prestations/participations/${id}/cancel`,
    method: MutationMethod.POST,
    invalidateQueries: [prestationsQueryKeys.all],
    successMessage: 'Dossier annulé',
  });
}

export function useUncancelPrestationParticipation(id: number) {
  return commiteeMutation<PrestationParticipationWithRelations>({
    url: `/commitee/:commiteeId/prestations/participations/${id}/uncancel`,
    method: MutationMethod.POST,
    invalidateQueries: [prestationsQueryKeys.all],
    successMessage: 'Dossier réactivé',
  });
}

export function useSendEmailToParticipants() {
  return commiteeMutation<
    { sentTo: number; skipped: number },
    SendParticipantsEmailData,
    SendParticipantsEmailData
  >({
    url: '/commitee/:commiteeId/prestations/participations/send-email',
    method: MutationMethod.POST,
    body: (data) => data,
    successMessage: 'Email envoyé avec succès',
  });
}

export function useChangePrestationAccount(prestationId: number) {
  return commiteeMutation<
    EcrituresRewrite,
    ChangePrestationAccountData,
    ChangePrestationAccountData
  >({
    url: `/commitee/:commiteeId/prestations/${prestationId}/account`,
    method: MutationMethod.PUT,
    invalidateQueries: [
      prestationsQueryKeys.one(prestationId),
      prestationsQueryKeys.account(prestationId),
      ...ECRITURES_REWRITE_INVALIDATIONS,
    ],
    body: (data) => data,
    successMessage: 'Compte de la prestation modifié',
  });
}
